import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  isValidSlug,
  type BoothAdminStateResponse,
  type BoothCaptureSummary,
} from '@photobooth/shared';
import {
  adminDeleteCapture,
  adminExtendBooth,
  adminRegeneratePages,
  fetchBoothAdminState,
  fetchBoothCaptures,
  requestBoothAdminLink,
  verifyBoothAdmin,
} from '../api/client';
import {
  clearAdminSession,
  loadAdminSession,
  saveAdminSession,
} from '../builder/adminSession';
import { Button } from '../ui/Button';
import { FooterCredit } from '../ui/FooterCredit';
import { Toast } from '../ui/Toast';
import { logger } from '../utils/log';
import { trackEvent } from '../utils/analytics';

const log = logger('booth-admin');

type Phase = 'loading' | 'signin' | 'sent' | 'verifying' | 'ready' | 'error';

export function BoothAdminPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const tokenParam = search.get('token');

  const [phase, setPhase] = useState<Phase>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [session, setSession] = useState<{ token: string; expiresAtMs: number } | null>(null);
  const [state, setState] = useState<BoothAdminStateResponse | null>(null);
  const [captures, setCaptures] = useState<BoothCaptureSummary[]>([]);
  const [toast, setToast] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(
    async (token: string) => {
      const [s, c] = await Promise.all([
        fetchBoothAdminState(slug, token),
        fetchBoothCaptures(slug),
      ]);
      setState(s);
      setCaptures(c.captures);
    },
    [slug],
  );

  // Boot: check for existing session, magic-link token, or fall back to sign-in.
  useEffect(() => {
    let cancelled = false;
    if (!isValidSlug(slug)) {
      setErrorMsg('Invalid booth URL.');
      setPhase('error');
      return;
    }
    const existing = loadAdminSession(slug);
    if (existing) {
      setSession(existing);
      (async () => {
        try {
          await refresh(existing.token);
          if (!cancelled) setPhase('ready');
        } catch (err) {
          log.warn('session refresh failed', err);
          clearAdminSession(slug);
          if (!cancelled) setPhase('signin');
        }
      })();
      return;
    }
    if (tokenParam) {
      setPhase('verifying');
      (async () => {
        try {
          const res = await verifyBoothAdmin(slug, { otp: tokenParam.toUpperCase() });
          saveAdminSession(slug, res.sessionToken, res.expiresAtMs);
          if (cancelled) return;
          setSession({ token: res.sessionToken, expiresAtMs: res.expiresAtMs });
          await refresh(res.sessionToken);
          if (cancelled) return;
          // Drop the token from the URL so it's not bookmarked / leaked.
          search.delete('token');
          setSearch(search, { replace: true });
          trackEvent('admin_signin_success', { slug });
          setPhase('ready');
        } catch (err) {
          log.warn('verify failed', err);
          if (!cancelled) {
            setErrorMsg(err instanceof Error ? err.message : 'Sign-in failed.');
            setPhase('signin');
          }
        }
      })();
      return;
    }
    setPhase('signin');
    return () => {
      cancelled = true;
    };
  }, [slug, tokenParam, refresh, search, setSearch]);

  const requestLink = async () => {
    setBusy(true);
    setErrorMsg(null);
    try {
      const r = await requestBoothAdminLink(slug);
      setSentTo(r.emailMasked);
      setPhase('sent');
      trackEvent('admin_link_sent', { slug });
    } catch (err) {
      trackEvent('admin_link_failed', { slug });
      setErrorMsg(err instanceof Error ? err.message : 'Could not send link.');
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async (id: string) => {
    if (!session) return;
    if (!confirm('Delete this capture? This cannot be undone.')) return;
    try {
      await adminDeleteCapture(slug, id, session.token);
      trackEvent('admin_capture_delete', { slug });
      setCaptures((prev) => prev.filter((c) => c.id !== id));
      setToast('Capture deleted');
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  const doExtend = async () => {
    if (!session) return;
    setBusy(true);
    try {
      const r = await adminExtendBooth(slug, session.token);
      trackEvent('admin_extend', { slug });
      setState((prev) => (prev ? { ...prev, expiresAt: r.expiresAt } : prev));
      setToast(`Extended — expires ${new Date(r.expiresAt).toLocaleDateString()}`);
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'Extend failed');
    } finally {
      setBusy(false);
    }
  };

  const doRegenerate = async () => {
    if (!session) return;
    setBusy(true);
    try {
      await adminRegeneratePages(slug, session.token);
      trackEvent('admin_regenerate_pages', { slug });
      setToast('Static pages regenerated');
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'Regenerate failed');
    } finally {
      setBusy(false);
    }
  };

  const signOut = () => {
    clearAdminSession(slug);
    setSession(null);
    setState(null);
    setCaptures([]);
    setPhase('signin');
  };

  if (phase === 'loading' || phase === 'verifying') {
    return <Centered>{phase === 'verifying' ? 'Signing you in…' : 'Loading…'}</Centered>;
  }
  if (phase === 'error') {
    return (
      <Centered>
        <h1>Can’t open admin</h1>
        <p style={{ color: 'var(--text-dim)' }}>{errorMsg}</p>
      </Centered>
    );
  }
  if (phase === 'signin') {
    return (
      <Centered>
        <h1>Admin for /{slug}</h1>
        <p style={{ color: 'var(--text-dim)', maxWidth: 420, textAlign: 'center' }}>
          To manage this booth, we’ll email a one-time sign-in link to the
          owner address on file.
        </p>
        {errorMsg && (
          <p style={{ color: 'var(--danger)', fontSize: 13 }}>{errorMsg}</p>
        )}
        <Button variant="primary" onClick={() => void requestLink()} disabled={busy}>
          {busy ? 'Sending…' : 'Email sign-in link'}
        </Button>
      </Centered>
    );
  }
  if (phase === 'sent') {
    return (
      <Centered>
        <h1>Check your email</h1>
        <p style={{ color: 'var(--text-dim)', maxWidth: 420, textAlign: 'center' }}>
          If <strong>{sentTo}</strong> is the owner email on file, we sent a
          sign-in link there. Open it within 15 minutes.
        </p>
      </Centered>
    );
  }

  // ready
  if (!state) return <Centered>Loading…</Centered>;
  const expiresDate = new Date(state.expiresAt).toLocaleDateString();
  const createdDate = new Date(state.createdAt).toLocaleDateString();

  return (
    <div
      style={{
        minHeight: '100dvh',
        maxWidth: 1000,
        margin: '0 auto',
        padding: '24px 16px 80px',
        color: 'var(--text)',
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: 16,
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: 24 }}>{state.title} — admin</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text-dim)', fontSize: 13 }}>
            Signed in as <strong>{state.emailMasked}</strong> · created {createdDate} · expires {expiresDate}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button variant="ghost" onClick={() => navigate(`/${slug}`)}>
            Open booth
          </Button>
          <Button variant="ghost" onClick={() => navigate(`/${slug}/gallery`)}>
            Gallery
          </Button>
          <Button variant="ghost" onClick={signOut}>
            Sign out
          </Button>
        </div>
      </header>

      <section style={sectionStyle}>
        <h2 style={sectionTitle}>Status</h2>
        <p style={{ margin: 0, color: 'var(--text-dim)' }}>
          {state.captureCount} {state.captureCount === 1 ? 'capture' : 'captures'} · password{' '}
          {state.hasPassword ? 'set' : 'not set'}
        </p>
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          <Button variant="primary" onClick={() => void doExtend()} disabled={busy}>
            Extend +30 days
          </Button>
          <Button variant="secondary" onClick={() => void doRegenerate()} disabled={busy}>
            Refresh static pages
          </Button>
          <Button variant="ghost" onClick={() => navigate(`/${slug}/delete?token=`)}>
            Delete this booth
          </Button>
        </div>
      </section>

      <section style={sectionStyle}>
        <h2 style={sectionTitle}>Captures</h2>
        {captures.length === 0 ? (
          <p style={{ color: 'var(--text-dim)', margin: 0 }}>None yet.</p>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
              gap: 12,
            }}
          >
            {captures.map((c) => (
              <div key={c.id} style={tileStyle}>
                {c.kind === 'photo' ? (
                  <img
                    src={c.mediaUrl}
                    alt=""
                    loading="lazy"
                    style={tileMediaStyle}
                  />
                ) : (
                  <video
                    src={c.mediaUrl}
                    muted
                    playsInline
                    preload="metadata"
                    style={tileMediaStyle}
                  />
                )}
                <button
                  type="button"
                  onClick={() => void doDelete(c.id)}
                  style={tileDeleteStyle}
                  aria-label="Delete capture"
                  title="Delete capture"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {toast && <Toast message={toast} />}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
        <FooterCredit />
      </div>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        padding: 24,
        textAlign: 'center',
        color: 'var(--text)',
      }}
    >
      {children}
    </div>
  );
}

const sectionStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.03)',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 16,
  padding: 16,
  marginBottom: 16,
};
const sectionTitle: React.CSSProperties = { margin: '0 0 8px', fontSize: 16 };
const tileStyle: React.CSSProperties = {
  position: 'relative',
  aspectRatio: '1 / 1',
  background: 'rgba(0,0,0,0.4)',
  borderRadius: 10,
  overflow: 'hidden',
  border: '1px solid rgba(255,255,255,0.08)',
};
const tileMediaStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
};
const tileDeleteStyle: React.CSSProperties = {
  position: 'absolute',
  top: 6,
  right: 6,
  width: 28,
  height: 28,
  borderRadius: 999,
  background: 'rgba(0,0,0,0.7)',
  color: '#fff',
  border: '1px solid rgba(255,255,255,0.2)',
  cursor: 'pointer',
  fontSize: 16,
  lineHeight: 1,
};
